import { useMutation, useQuery } from '@tanstack/react-query';
import { queryClient } from 'app/app';
import { showToast } from 'app/shared/util/toast.util';
import {
  copyParties,
  deleteParty,
  fetchParties,
  fetchPartiesForAssignee,
  getPartiesData,
  getParty,
  savePartiesData,
  saveParty,
} from 'app/edozvola/cases/parties/service/party.service';
import { toast } from 'react-toastify';
import { PaginationState } from 'app/shared/pagination/pagination.model';
import { PartiesModel, PartyModel } from '../model/party.model';
import { messages, TABLE_QUERY_KEYS } from 'app/config/constants';
import success = toast.success;
import error = toast.error;

export const fetchPartiesDataQuery = (idCase: string) => {
  return useQuery({
    queryKey: ['parties-data', idCase],
    queryFn: () => getPartiesData(idCase),
  });
};

export const savePartiesDataQuery = (caseUuid: string, onSuccessCallback: any) => {
  return useMutation({
    mutationFn: (partiesData: PartiesModel) => savePartiesData(caseUuid, partiesData),
    onSuccess: () => {
      onSuccessCallback();
      queryClient.invalidateQueries({ queryKey: ['parties-data', caseUuid] });
      showToast(success, messages.DATA_SAVED);
    },
    onError: (err: any) => {
      showToast(error, err);
    },
  });
};

export const copyPartiesFromCaseQuery = (caseUuid: string, onSuccessCallback: any) => {
  return useMutation({
    mutationFn: (caseUuidToCopyFrom: string) => copyParties(caseUuid, caseUuidToCopyFrom),
    onSuccess: () => {
      onSuccessCallback();
      queryClient.invalidateQueries({ queryKey: [TABLE_QUERY_KEYS.PARTIES_TABLE] });
      showToast(success, messages.DATA_COPIED);
    },
    onError: (err: any) => {
      showToast(error, err);
    },
  });
};

export const fetchPartiesQuery = (pagination: PaginationState, idCase: string) => {
  return useQuery({
    queryKey: [TABLE_QUERY_KEYS.PARTIES_TABLE, { idCase: idCase, pagination: { ...pagination } }],
    queryFn: ({ signal }) => fetchParties(idCase, pagination.page, pagination.size, pagination.column, pagination.direction),
  });
};

export const deletePartyQuery = () => {
  return useMutation({
    mutationFn: (idParty: number) => deleteParty(idParty),
    onSuccess: () => {
      showToast(success, messages.DATA_DELETED);
      queryClient.invalidateQueries({ queryKey: [TABLE_QUERY_KEYS.PARTIES_TABLE] });
    },
    onError: (err: any) => {
      showToast(error, err);
    },
  });
};

export const fetchPartyQuery = (idParty: string) => {
  return useQuery({
    queryKey: ['party', idParty],
    queryFn: () => getParty(idParty),
    enabled: !!idParty,
  });
};

export const savePartyQuery = (idCase: string, onSuccessCallback) => {
  return useMutation({
    mutationFn: (party: PartyModel) => {
      return saveParty(idCase, party);
    },
    onSuccess: () => {
      showToast(success, messages.DATA_SAVED);
      queryClient.invalidateQueries({ queryKey: ['party'] });
      queryClient.invalidateQueries({ queryKey: [TABLE_QUERY_KEYS.PARTIES_TABLE] });
      onSuccessCallback();
    },
    onError: (err: any) => {
      showToast(error, err);
    },
  });
};

export const fetchPartiesForAssigneeQuery = (idCase: string, shouldFetch: boolean, idParty: string) => {
  return useQuery({
    queryKey: ['parties-for-assignee', idCase],
    queryFn: () => fetchPartiesForAssignee(idCase, idParty),
    enabled: shouldFetch,
  });
};
